import * as React from 'react';
import { ArrowBack, FilterList } from '@mui/icons-material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  InputAdornmentProps,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers';
import {
  ChangeEvent,
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import TitledDrawer from '../../../../components/TitledDrawer';
import CustomPeriodPanel from '../../../../components/CustomPeriodPanel';
import useCustomTimePeriodMenuItems from '../../../../components/CustomTimePeriodMenuItems';
import {
  comboStyle,
  desktopButtonStyle,
  desktopSecondaryButtonStyle,
  mobileButtonStyle,
  mobileSecondaryButtonStyle,
} from '../../../../constants';
import { CustomTimePeriod, useCustomTimePeriods } from '../../../../api/customTimePeriods';
import { isPresetPeriod, TInputDateToString } from '../../../../helpers/dateUtils';
import { defer } from '../../../../helpers/helpers';
import { UIContext } from '../../../../context/UIContext';
import { DashboardFiltersContext } from './DashboardFiltersContext';
import { DashboardFilters } from '../../../../models/dashboard';
import { DashboardContext } from '../../DashboardContext';
import MultipleSelectCheckmarks from '../MultipleSelectCheckmarks';

const subHeaderStyle: CSSProperties = {
  fontFamily: 'Open Sans',
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: '16px',
};

const BottomToolbar = () => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useContext(UIContext);
  const {
    applyFilters,
    filtersChanged,
    resetFilters,
    closeFilters,
  } = useContext(DashboardFiltersContext);
  const buttonStyle = (isMobile || isTablet) ? mobileButtonStyle : desktopButtonStyle;
  const secondaryButtonStyle = isMobile ? mobileSecondaryButtonStyle : desktopSecondaryButtonStyle;

  return (
    <Box
      sx={{
        display: 'flex', 
        flexDirection: (isMobile || isTablet) ? 'column' : 'row-reverse',
        marginLeft: 'auto',
        padding: '16px'
      }}
    >
      <Button
        variant="contained"
        sx={[buttonStyle]}
        onClick={() => {
          if (applyFilters()) defer(() => closeFilters(true));
        }}
        data-qa-id="filterApplyButton"
      >
        {t('components.filters.apply')}
      </Button>
      {filtersChanged && (
        <Button
          variant="text"
          sx={{ ...secondaryButtonStyle }}
          onClick={resetFilters}
          data-qa-id="filterResetButton"
        >
          {t('components.filters.reset')}
        </Button>
      )}
    </Box>
  );
}

const DashboardFiltersPanel = () => {
  const {
    unsavedFilters,
    setFilters,
    setFiltersStateless,
    closeFilters,
    validationErrors,
    dateRangeConfig,
  } = useContext(DashboardFiltersContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const { isMobile, isTablet } = useContext(UIContext);
  const { companyConfig: filtersData, fetchCompanyConfig: fetchFilters } = useContext(DashboardContext);
  const [availableLocations, setAvailableLocations] = useState(filtersData?.locations);
  useEffect(() => {
    setAvailableLocations(filtersData?.locations.filter(location => !unsavedFilters?.locationArea || unsavedFilters.locationArea.id === location.locationAreaId));
  }, [unsavedFilters?.locationArea])

  const errorsFor = useCallback(
    (key: string) =>
      validationErrors
        .filter((it) => it.field === key)
        .map((it) => it.message)
        .join(', '),
    [validationErrors]
  );
  const hasErrors = useCallback((key: string) => errorsFor(key).length > 0, [errorsFor]);
  const onChangePeriod = useCallback(
    (ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const nextPeriod = ev.target.value;
      if (!(nextPeriod === 'custom' || isPresetPeriod(nextPeriod))) return;
      
      setFilters(
        (prevFilters: DashboardFilters) =>
          ({
            ...prevFilters,
            period: nextPeriod,
          })
      );
    },
    [setFilters]
  );
  const onChangePeriodFrom = useCallback(
    (date: Date | undefined | null) =>
      setFilters(
        (prevFilters: DashboardFilters) =>
          ({
            ...prevFilters,
            periodFrom: TInputDateToString(date),
          } as DashboardFilters)
      ),
    [setFilters]
  );
  const onChangePeriodTo = useCallback(
    (date: Date | undefined | null) =>
      setFilters(
        (prevFilters: DashboardFilters) =>
          ({
            ...prevFilters,
            periodTo: TInputDateToString(date),
          } as DashboardFilters)
      ),
    [setFilters]
  );
  const onChangeComparisionOpen = useCallback(
    (comparisonOpen: boolean) => setFilters(
      (prevFilters: DashboardFilters) =>
        ({
          ...prevFilters,
          comparisonOpen
        } as DashboardFilters)
    ),
  [setFilters]
  );

  const handleSwitchChange = () => {
    if(unsavedFilters.comparisonOpen) {
      onChangeComparisionOpen(false);
    } else {
      onChangeComparisionOpen(true);
    }
  };
  
  const [openPeriodFrom, setOpenPeriodFrom] = useState(false);
  const [openPeriodTo, setOpenPeriodTo] = useState(false);

  const pickerFrom = useMemo(
    () => (unsavedFilters.periodFrom ? new Date(unsavedFilters.periodFrom) : new Date()),
    [unsavedFilters.periodFrom]
  );
  const pickerTo = useMemo(
    () => (unsavedFilters.periodTo ? new Date(unsavedFilters.periodTo) : new Date()),
    [unsavedFilters.periodTo]
  );
  const customStartPicker = useCallback(
    (p: InputAdornmentProps) => (
      <InputAdornment {...p} position="end" onClick={() => setOpenPeriodFrom(true)}>
        <IconButton aria-label="Period from (show calendar)" sx={{ left: '12px' }}>
          {hasErrors('periodFrom') ? <ErrorOutlineIcon color="error" /> : <EventOutlinedIcon />}
        </IconButton>
      </InputAdornment>
    ),
    [setOpenPeriodFrom, hasErrors]
  );
  const customEndPicker = useCallback(
    (p: InputAdornmentProps) => (
      <InputAdornment {...p} position="end" onClick={() => setOpenPeriodTo(true)}>
        <IconButton aria-label="Period to (show calendar)" sx={{ left: '12px' }}>
          {hasErrors('periodTo') ? <ErrorOutlineIcon color="error" /> : <EventOutlinedIcon />}
        </IconButton>
      </InputAdornment>
    ),
    [setOpenPeriodTo, hasErrors]
  );

  const onChangeComparePeriod = useCallback(
    (ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const nextPeriod = ev.target.value;
      if (!(nextPeriod === 'custom' || isPresetPeriod(nextPeriod))) return;

      setFilters(
        (prevFilters: DashboardFilters) =>
          ({
            ...prevFilters,
            comparePeriod: nextPeriod,
          })
      );
    },
    [setFilters]
  );
  const onChangeComparePeriodFrom = useCallback(
    (date: Date | undefined | null) =>
      setFilters(
        (prevFilters: DashboardFilters) =>
          ({
            ...prevFilters,
            comparePeriodFrom: TInputDateToString(date),
          } as DashboardFilters)
      ),
    [setFilters]
  );
  const onChangeComparePeriodTo = useCallback(
    (date: Date | undefined | null) =>
      setFilters(
        (prevFilters: DashboardFilters) =>
          ({
            ...prevFilters,
            comparePeriodTo: TInputDateToString(date),
          } as DashboardFilters)
      ),
    [setFilters]
  );
  const [openComparePeriodFrom, setOpenComparePeriodFrom] = useState(false);
  const [openComparePeriodTo, setOpenComparePeriodTo] = useState(false);

  const pickerCompareFrom = useMemo(
    () => (unsavedFilters.comparePeriodFrom ? new Date(unsavedFilters.comparePeriodFrom) : new Date()),
    [unsavedFilters.comparePeriodFrom]
  );
  const pickerCompareTo = useMemo(
    () => (unsavedFilters.comparePeriodTo ? new Date(unsavedFilters.comparePeriodTo) : new Date(Date.now())),
    [unsavedFilters.comparePeriodTo]
  );
  const customCompareStartPicker = useCallback(
    (p: InputAdornmentProps) => (
      <InputAdornment {...p} position="end" onClick={() => setOpenComparePeriodFrom(true)}>
        <IconButton aria-label="Compare period from (show calendar)" sx={{ left: '12px' }}>
          {hasErrors('comparePeriodFrom') ? <ErrorOutlineIcon color="error" /> : <EventOutlinedIcon />}
        </IconButton>
      </InputAdornment>
    ),
    [setOpenComparePeriodFrom, hasErrors]
  );
  const customCompareEndPicker = useCallback(
    (p: InputAdornmentProps) => (
      <InputAdornment {...p} position="end" onClick={() => setOpenComparePeriodTo(true)}>
        <IconButton aria-label="Compare period to (show calendar)" sx={{ left: '12px' }}>
          {hasErrors('comparePeriodTo') ? <ErrorOutlineIcon color="error" /> : <EventOutlinedIcon />}
        </IconButton>
      </InputAdornment>
    ),
    [setOpenComparePeriodTo, hasErrors]
  );

  const fireResize = useCallback(() => {
    if (typeof Event === 'function') {
      // modern browsers
      window.dispatchEvent(new Event('resize'));
    } else {
      // for IE and other old browsers
      // causes deprecation warning on modern browsers
      const evt = window.document.createEvent('UIEvents');
      evt.initUIEvent('resize', true, false, window, 0);
      window.dispatchEvent(evt);
    }
  }, []);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  const { data: customPeriods = [] } = useCustomTimePeriods();
  const [customTimePeriodOpen, setCustomTimePeriodOpen] = useState(false);
  const [periodPanel, setPeriodPanel] = useState<CustomTimePeriod | undefined>();

  const onCreateCustomPeriod = useCallback(() => {
    setPeriodPanel(undefined);
    setCustomTimePeriodOpen(true);
  }, []);

  const onEditCustomPeriod = useCallback((customPeriod: CustomTimePeriod) => {
    setPeriodPanel(customPeriod);
    setCustomTimePeriodOpen(true);
  }, []);

  const onSelectCustomPeriod = useCallback(
    (period: CustomTimePeriod) => {
      setFiltersStateless((prevFilters: DashboardFilters) => ({
        ...prevFilters,
        period: period.id,
        periodFrom: TInputDateToString(period.startDate),
        periodTo: TInputDateToString(period.endDate),
      }));
    },
    [setFiltersStateless]
  );

  const customTimePeriodMenuItems = useCustomTimePeriodMenuItems({
    customPeriods,
    onSelect: onSelectCustomPeriod,
    onEdit: onEditCustomPeriod,
    onCreate: onCreateCustomPeriod,
  });

  const [customCompareTimePeriodOpen, setCustomCompareTimePeriodOpen] = useState(false);

  const onCreateCustomComparePeriod = useCallback(() => {
    setPeriodPanel(undefined);
    setCustomCompareTimePeriodOpen(true);
  }, []);

  const onEditCustomComparePeriod = useCallback((customPeriod: CustomTimePeriod) => {
    setPeriodPanel(customPeriod);
    setCustomCompareTimePeriodOpen(true);
  }, []);

  const onSelectCustomComparePeriod = useCallback(
    (period: CustomTimePeriod) => {
      setFiltersStateless((prevFilters: DashboardFilters) => ({
        ...prevFilters,
        comparePeriod: period.id,
        comparePeriodFrom: TInputDateToString(period.startDate),
        comparePeriodTo: TInputDateToString(period.endDate),
      }));
    },
    [setFiltersStateless]
  );

  const onDeleteCustomPeriod = useCallback(
    (periodId: string) => {
      setFiltersStateless((prevFilters: DashboardFilters) =>
        prevFilters.period === periodId
          ? { ...prevFilters, period: 'custom' }
          : prevFilters
      );
    },
    [setFiltersStateless]
  );

  const onDeleteCustomComparePeriod = useCallback(
    (periodId: string) => {
      setFiltersStateless((prevFilters: DashboardFilters) =>
        prevFilters.comparePeriod === periodId
          ? { ...prevFilters, comparePeriod: 'custom' }
          : prevFilters
      );
    },
    [setFiltersStateless]
  );

  const customCompareTimePeriodMenuItems = useCustomTimePeriodMenuItems({
    customPeriods,
    onSelect: onSelectCustomComparePeriod,
    onEdit: onEditCustomComparePeriod,
    onCreate: onCreateCustomComparePeriod,
  });

  if (customTimePeriodOpen) {
    return (
      <CustomPeriodPanel
        initialPeriod={periodPanel}
        dateRangeConfig={dateRangeConfig}
        onClose={() => setCustomTimePeriodOpen(false)}
        onSave={onSelectCustomPeriod}
        onDelete={onDeleteCustomPeriod}
      />
    );
  }

  if (customCompareTimePeriodOpen) {
    return (
      <CustomPeriodPanel
        initialPeriod={periodPanel}
        dateRangeConfig={dateRangeConfig}
        onClose={() => setCustomCompareTimePeriodOpen(false)}
        onSave={onSelectCustomComparePeriod}
        onDelete={onDeleteCustomComparePeriod}
      />
    );
  }

  return (
    <TitledDrawer
      title={t('components.filters.filters')}
      onClose={() => closeFilters()}
      icon={
        !isMobile && !isTablet ?
        <FilterList
          sx={{
            color: 'text.secondary',
          }}
        /> :
        <ArrowBack
          sx={{
            color: 'text.secondary'
          }}
        />
      }
      bottomToolbar={<BottomToolbar />}
      bottomToolbarMarginTop='16px'
      iconRight={(isMobile || isTablet) ?
        <FilterList
          sx={{
            color: 'text.secondary'
          }}
        /> : undefined
      }
    >
      <Grid
        item
        sx={{
          paddingLeft: '4px',
          paddingRight: '4px'
        }}
      >
        <Accordion
          elevation={0}
          defaultExpanded
          disableGutters
          square
          sx={{
            padding: 0,
            border: 0,
            margin: 0,
          }}
          onClick={() => setTimeout(fireResize, 250)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="timeFilters-content"
            sx={{ padding: 0, border: 0, margin: 0 }}
            id="timeFilters-header"
            data-qa-id="timeFilterHeader"
          >
            <Typography sx={subHeaderStyle} variant="h3">
              {t('components.filters.time')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0, border: 0, margin: 0 }}>
            <TextField
              size="small"
              sx={comboStyle}
              select
              label={t('components.datePicker.timePeriod')}
              value={unsavedFilters.period}
              id="period"
              onChange={onChangePeriod}
              inputProps={{'data-qa-id':"filterPeriodOption"}}
            >
              <MenuItem value="today">{t('components.datePicker.today')}</MenuItem>
              <MenuItem value="yesterday">{t('components.datePicker.yesterday')}</MenuItem>
              <MenuItem value="thisWeek">{t('components.datePicker.thisWeek')}</MenuItem>
              <MenuItem value="lastWeek">{t('components.datePicker.lastWeek')}</MenuItem>
              <MenuItem value="thisMonth">{t('components.datePicker.thisMonth')}</MenuItem>
              <MenuItem value="lastMonth">{t('components.datePicker.lastMonth')}</MenuItem>
              <MenuItem value="custom">{t('components.datePicker.custom')}</MenuItem>
              {customTimePeriodMenuItems}
            </TextField>
            <DateTimePicker
              label={t('components.datePicker.startDate')}
              value={pickerFrom}
              onChange={onChangePeriodFrom}
              open={openPeriodFrom}
              onOpen={() => setOpenPeriodFrom(true)}
              onClose={() => setOpenPeriodFrom(false)}
              slots={{
                inputAdornment: customStartPicker,
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  error: hasErrors('periodFrom'),
                  helperText:
                    errorsFor('periodFrom') ||
                    `${t('components.datePicker.aFullDayIsFrom')} ${dateRangeConfig.daysStartAt
                      .toString(10)
                      .padStart(2, '0')}:00 ${t(
                      'components.datePicker.to'
                    )} ${dateRangeConfig.daysStartAt.toString(10).padStart(2, '0')}:00`,
                  style: comboStyle,
                  inputProps: {
                    'data-qa-id': "filterStartDateTime"
                  }
                },
              }}
              format={t('components.datePicker.dateTimeFormat')}
            />
            <DateTimePicker
              label={t('components.datePicker.endDate')}
              value={pickerTo}
              onChange={onChangePeriodTo}
              open={openPeriodTo}
              onOpen={() => setOpenPeriodTo(true)}
              onClose={() => setOpenPeriodTo(false)}
              format={t('components.datePicker.dateTimeFormat')}
              slots={{
                inputAdornment: customEndPicker,
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  error: hasErrors('periodTo'),
                  style: comboStyle,
                  helperText:
                    errorsFor('periodTo') ||
                    `${t('components.datePicker.aFullDayIsFrom')} ${dateRangeConfig.daysStartAt
                      .toString(10)
                      .padStart(2, '0')}:00 ${t(
                      'components.datePicker.to'
                    )} ${dateRangeConfig.daysStartAt.toString(10).padStart(2, '0')}:00`,
                  inputProps: {
                    'data-qa-id': "filterStartDateTime"
                  }
                },
              }}
            />
          </AccordionDetails>
        </Accordion>

            <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
              <Typography sx={[subHeaderStyle, {paddingTop: '8px', marginBottom: '16px'}]} variant="h3">
                {t('components.filters.timeComparison')}
              </Typography>
              <Switch 
                checked={unsavedFilters.comparisonOpen}
                onChange={handleSwitchChange}
                />
            </Box>
            <Box sx={{height: '32px'}}>
            <Typography variant='body2' sx={{color: theme.palette.text.secondary}}>{t('components.filters.timeComparisonDescription')}</Typography>
            </Box>
            {unsavedFilters.comparisonOpen &&
            <>
             <TextField
              size="small"
              sx={comboStyle}
              select
              label={t('components.datePicker.timePeriod')}
              value={unsavedFilters.comparePeriod}
              id="comparePeriod"
              onChange={onChangeComparePeriod}
              inputProps={{'data-qa-id':"filterComparePeriodOption"}}
            >
              <MenuItem value="today">{t('components.datePicker.today')}</MenuItem>
              <MenuItem value="yesterday">{t('components.datePicker.yesterday')}</MenuItem>
              <MenuItem value="thisWeek">{t('components.datePicker.thisWeek')}</MenuItem>
              <MenuItem value="lastWeek">{t('components.datePicker.lastWeek')}</MenuItem>
              <MenuItem value="thisMonth">{t('components.datePicker.thisMonth')}</MenuItem>
              <MenuItem value="lastMonth">{t('components.datePicker.lastMonth')}</MenuItem>
              <MenuItem value="custom">{t('components.datePicker.custom')}</MenuItem>
              {customCompareTimePeriodMenuItems}
            </TextField>
            <DateTimePicker
              label={t('components.datePicker.startDate')}
              value={pickerCompareFrom}
              onChange={onChangeComparePeriodFrom}
              open={openComparePeriodFrom}
              onOpen={() => setOpenComparePeriodFrom(true)}
              onClose={() => setOpenComparePeriodFrom(false)}
              slots={{
                inputAdornment: customCompareStartPicker,
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  error: hasErrors('comparePeriodFrom'),
                  helperText:
                    errorsFor('comparePeriodFrom') ||
                    `${t('components.datePicker.aFullDayIsFrom')} ${dateRangeConfig.daysStartAt
                      .toString(10)
                      .padStart(2, '0')}:00 ${t(
                      'components.datePicker.to'
                    )} ${dateRangeConfig.daysStartAt.toString(10).padStart(2, '0')}:00`,
                  style: comboStyle,
                  inputProps: {
                    'data-qa-id': "filterCompareStartDateTime"
                  }
                },
              }}
              format={t('components.datePicker.dateTimeFormat')}
            />
            <DateTimePicker
              label={t('components.datePicker.endDate')}
              value={pickerCompareTo}
              onChange={onChangeComparePeriodTo}
              open={openComparePeriodTo}
              onOpen={() => setOpenComparePeriodTo(true)}
              onClose={() => setOpenComparePeriodTo(false)}
              format={t('components.datePicker.dateTimeFormat')}
              slots={{
                inputAdornment: customCompareEndPicker,
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  error: hasErrors('comparePeriodTo'),
                  style: comboStyle,
                  helperText:
                    errorsFor('comparePeriodTo') ||
                    `${t('components.datePicker.aFullDayIsFrom')} ${dateRangeConfig.daysStartAt
                      .toString(10)
                      .padStart(2, '0')}:00 ${t(
                      'components.datePicker.to'
                    )} ${dateRangeConfig.daysStartAt.toString(10).padStart(2, '0')}:00`,
                  inputProps: {
                    'data-qa-id': "filterCompareStartDateTime"
                  }
                },
              }}
            />
          </> }
        <Accordion
          elevation={0}
          square
          disableGutters
          sx={{ padding: 0, border: 0, margin: 0 }}
          onClick={() => setTimeout(fireResize, 250)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ padding: 0, border: 0, margin: 0 }}
            aria-controls="areaFilters-content"
            id="areaFilters-header"
            data-qa-id="areaFilterHeader"
          >
            <Typography sx={subHeaderStyle} variant="h3">
              {t('components.filters.area')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0, border: 0, margin: 0 }}>
            <TextField
              size="small"
              select
              value={
                unsavedFilters.locationArea ? JSON.stringify(unsavedFilters.locationArea) : 'all'
              }
              label={t('components.filters.locationArea')}
              id="locationArea"
              sx={comboStyle}
              error={hasErrors('locationArea')}
              helperText={errorsFor('locationArea')}
              onChange={(ev) =>
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  locationArea: ev.target.value === 'all' ? undefined : JSON.parse(ev.target.value),
                }))
              }
              inputProps={{'data-qa-id':"filterLocationAreaOption"}}
            >
              <MenuItem value="all">{t('components.filters.allLocationAreas')}</MenuItem>
              {filtersData &&
                filtersData.locationAreas &&
                filtersData.locationAreas.map((la) => (
                  <MenuItem value={JSON.stringify(la)} key={la.id}>
                    {la.name}
                  </MenuItem>
                ))}
            </TextField>
            {availableLocations && <MultipleSelectCheckmarks 
              ids={availableLocations
                .map(location => location.id) || []}
              names={availableLocations
                .map(location => location.name) || []}
            />}
          </AccordionDetails>
        </Accordion>
      </Grid>
    </TitledDrawer>
  );
};

export default DashboardFiltersPanel;
